import {Component, Inject, OnInit,} from '@angular/core';
import {MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef} from "@angular/material/legacy-dialog";
import {StationInfo} from "../model/StationInfo";
import {AdditionalStationInfoService} from "../additional-station-info/additional-station-info-service"
import {ImageService, ImageUrl} from "../images/image.service";
import {
  extractIconFileName,
  findImageUrlForLabel,
  renameFileForInfoLabel
} from "../images/image-label.utils";
import {Observable, of} from "rxjs";



@Component({
  selector: 'met-add-station-info-dialog',
  templateUrl: './add-station-info-dialog.component.html',
  styleUrls: ['./add-station-info-dialog.component.scss']
})
export class AddStationInfoDialogComponent implements OnInit {


  active: boolean = false;
  selectedFile: File | null = null;
  iconPreview: string | ArrayBuffer | null = null;
  errorMessage: string | undefined;
  existingNames: string[] = [];
  nameAlreadyExists: boolean = false;
  imageUrls$: Observable<ImageUrl[]> = of([]);
  defaultIcon = 'assets/default-icon.png';
  originalLabel?: string;
  originalIconUrl?: string;


  constructor(
    public dialogRef: MatDialogRef<AddStationInfoDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: StationInfo,
    private additionalStationInfoService: AdditionalStationInfoService,
    private imageService: ImageService) {

  }

  ngOnInit(): void {
    this.originalLabel = this.data?.infoLabel ?? '';
    if (this.data) {
      this.active = this.data.active ?? false;

    }

    this.imageUrls$ = this.imageService.getStationImageUrls("stationInfo");

    if (this.data?.id && this.data?.infoLabel) {
      this.loadExistingIcon();
    }

    this.additionalStationInfoService.getAll(0,10, "").subscribe({
      next: (entries: StationInfo[]) => {
        this.existingNames = entries
          .filter(e => e.id !== this.data.id)
          .map(e => e.infoLabel.trim());
      },
      error: err => console.error("Fehler beim Laden der Einträge", err)
    });
  }

  checkName(): void {
    const name = this.data.infoLabel?.trim() || '';
    this.nameAlreadyExists = this.existingNames
      .some(existingName => existingName.toLowerCase() === name.toLowerCase());

    if (!name) {
      this.errorMessage = 'Label darf nicht leer sein!';
    } else if (this.nameAlreadyExists) {
      this.errorMessage = 'Dieser Eintrag existiert bereits!';
    } else {
      this.errorMessage = undefined;
    }
  }


  onCancel(): void {
    this.dialogRef.close();
  }

  async onSave(): Promise<void> {
    if (!this.selectedFile && !this.data?.id) {
      this.errorMessage = "Bitte wählen Sie ein Icon aus.";
      return;
    }

    if (this.nameAlreadyExists) {
      this.errorMessage = "Ein Eintrag mit diesem Namen existiert bereits!";
      return;
    }

    const stationInfo: StationInfo = {
      ...this.data,
      infoLabel: this.data.infoLabel?.trim() ?? '',
      active: this.active,
      stationId: this.data.stationId || 0
    };

    this.dialogRef.close(true);

    const nameChanged = (this.originalLabel ?? '').trim().toLowerCase() !== (this.data.infoLabel ?? '').trim().toLowerCase();
    const uploadFile = this.prepareIconFile(stationInfo);
    const originalIconName = this.getOriginalIconName(nameChanged);

    if (stationInfo.id) {
      this.additionalStationInfoService
        .edit(stationInfo, uploadFile, originalIconName)
        .subscribe({
          next: () => {},
        });
    } else {
      this.additionalStationInfoService
        .create(stationInfo, uploadFile!)
        .subscribe({
          next: () => {},
        });
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.[0]) {
      const file = input.files[0];
      if (file.type !== 'image/png' && !file.name.toLowerCase().endsWith('.png')) {
        this.errorMessage = "Nur PNG-Dateien sind erlaubt!";
        this.selectedFile = null;
        this.iconPreview = null;
        return;
      }

      this.selectedFile = file;
      this.previewIcon();
    }
  }

  previewIcon(): void {
    if (!this.selectedFile) return;

    const reader = new FileReader();
    reader.onload = () => {
      this.iconPreview = reader.result as string;
    };
    reader.readAsDataURL(this.selectedFile);
  }

  get isFormValid(): boolean {
    const hasLabel = !!this.data.infoLabel?.trim();
    const nameUnique = !this.nameAlreadyExists;

    if (this.data?.id) {
      return hasLabel && nameUnique;
    } else {
      return hasLabel && nameUnique && !!this.iconPreview;
    }
  }

  loadExistingIcon(): void {
    this.imageUrls$.subscribe(images => {
      if (this.data.infoLabel && images.length > 0) {
        const existingImageUrl = this.getImageUrlForLabel(this.data.infoLabel, images);
        if (existingImageUrl !== this.defaultIcon) {
          this.iconPreview = existingImageUrl;
          this.originalIconUrl = existingImageUrl;
        }
      }
    });
  }

  getImageUrlForLabel(infoLabel: string, images: ImageUrl[]): string {
    return findImageUrlForLabel(infoLabel, images, this.defaultIcon);
  }

  onImageError(event: Event): void {
    (event.target as HTMLImageElement).src = this.defaultIcon;
  }

  private prepareIconFile(info: StationInfo): File | undefined {
    if (!this.selectedFile) {
      return undefined;
    }
    return renameFileForInfoLabel(this.selectedFile, info.infoLabel ?? '');
  }

  private getOriginalIconName(nameChanged: boolean): string | undefined {
    if (!nameChanged || this.selectedFile || !this.originalIconUrl) {
      return undefined;
    }
    return extractIconFileName(this.originalIconUrl) || undefined;
  }

}
